import { useState } from "react";
import { useTenantTerms } from "../../../lib/TenantConfigContext";
import {
  peopleAtWorkshop,
  personRemovalNote,
  snapshotRoleLabel,
  unfilledRoles,
  workshopRow,
} from "../../../lib/organization";
import { usePersonRemoval } from "../../SuperAdmin/useOrgEditors";
import { Icon } from "../../../components/msd/Icon";
import { Avatar, Btn, Sheet, Switch, cx } from "../../../components/msd/ui";
import PersonSheet from "./PersonSheet";
import { ConfirmDelete, FieldGroup, RemoveBtn, TG_LIST, TG_ROW, plural } from "./kit";

/**
 * One workshop: who works there, whether it is live, and deleting it — what
 * Super Admin's WorkshopDetail and the Workshops row offer, in a sheet.
 *
 * @param editor  useWorkshopEditor(), shared with the page
 */
export default function WorkshopSheet({ ix, workshopId, onClose, editor, canEdit, canEditPeople }) {
  const { roleLabel: contextRoleLabel } = useTenantTerms();
  const roleLabel = (code) => snapshotRoleLabel(ix, code, contextRoleLabel);
  const removal = usePersonRemoval(null, ix);
  // The person in the person sheet: false when closed, null when adding.
  const [person, setPerson] = useState(false);

  const workshop = workshopId ? ix.workshopById.get(workshopId) : null;
  if (!workshop) return null;

  const location = workshop.workshop_location;
  const row = workshopRow(workshop, ix);
  // Service advisors are listed with everyone else: same row, same edit and
  // remove, so there is one way to look after anyone at the workshop.
  const staff = peopleAtWorkshop(location, ix);
  const unfilled = unfilledRoles(location, ix);
  const dealer = workshop.dealer_id ? ix.dealerById.get(workshop.dealer_id) : null;
  const paused = row.status === "paused";

  const deleteWorkshop = async () => {
    if (await editor.confirmDelete()) onClose();
  };
  const removePerson = async () => {
    if (await removal.confirmRemove()) setPerson(false);
  };

  // One thing on screen at a time, so Escape closes only the top one.
  const covered = person !== false || !!removal.deleting || !!editor.deleting;

  return (
    <>
      <Sheet
        open={!covered}
        title={location}
        onClose={onClose}
        testid="workshop-sheet"
        footer={
          <>
            {canEdit && <RemoveBtn onClick={() => editor.askDelete(workshop.id)}>Delete workshop</RemoveBtn>}
            <Btn variant="pri" onClick={onClose}>
              Done
            </Btn>
          </>
        }
      >
        <div className="-mt-2 text-[13px] text-msd-muted">
          {dealer ? dealer.name : "No dealer"} · {plural(staff.length, "person", "people")}
        </div>

        <div className={cx(TG_ROW, "rounded-[14px] shadow-msd-e1")}>
          <span className="grow">
            <b className="block text-[15px] font-semibold">Workshop status</b>
            <span className="block text-[13px] text-msd-muted">{paused ? "Paused" : "Active"}</span>
          </span>
          {canEdit ? (
            <Switch
              on={!paused}
              onChange={() => editor.toggleStatus(row)}
              label="Pause or resume this workshop"
            />
          ) : null}
        </div>

        <FieldGroup
          label="Team"
          hint={unfilled.length ? `Not staffed: ${unfilled.map((k) => roleLabel(k)).join(", ")}` : undefined}
        >
          {staff.length ? (
            <div className={TG_LIST}>
              {staff.map((p) => {
                const name = p.name || (p.email || "").split("@")[0];
                const inner = (
                  <>
                    <Avatar name={name} size={34} />
                    <span className="min-w-0 grow">
                      <b className="block truncate text-[15px] font-semibold">{name}</b>
                      <span className="block truncate text-[13px] text-msd-muted">
                        {roleLabel(p.role)} · {p.email || "No login"}
                      </span>
                      <span className="block truncate text-[13px] tabular-nums text-msd-muted">
                        {p.number || "No number"}
                      </span>
                    </span>
                  </>
                );
                return canEditPeople ? (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPerson(p)}
                    aria-label={`Edit ${p.name || p.email}`}
                    data-testid="workshop-person"
                    className={cx(TG_ROW, "w-full cursor-pointer border-0 text-left text-inherit hover:bg-msd-soft")}
                  >
                    {inner}
                    <span className="flex text-msd-faint">
                      <Icon name="right" size={15} stroke={2} />
                    </span>
                  </button>
                ) : (
                  <div key={p.id} className={TG_ROW} data-testid="workshop-person">
                    {inner}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[13px] text-msd-muted">Nobody is recorded at this workshop yet.</div>
          )}
          {canEditPeople && (
            <button
              type="button"
              onClick={() => setPerson(null)}
              className="flex cursor-pointer items-center gap-2 rounded-[14px] border-[1.5px] border-dashed border-msd-line2 bg-transparent px-[14px] py-3 text-[14px] font-semibold text-[#3A5F94]"
            >
              <Icon name="plus2" size={15} stroke={2.4} />
              Add person
            </button>
          )}
        </FieldGroup>
      </Sheet>

      <PersonSheet
        open={person !== false && !removal.deleting}
        onOpenChange={(v) => !v && setPerson(false)}
        ix={ix}
        person={person || null}
        atWorkshop={person ? null : location}
        onRemove={(p) => removal.askRemove(p.id, p.has_login)}
      />

      <ConfirmDelete
        open={!!removal.deleting}
        onClose={() => removal.setDeleting(null)}
        name={removal.deleting?.name || ""}
        body={personRemovalNote(removal.deleting?.hasLogin)}
        onConfirm={removePerson}
        pending={removal.pending}
      />

      <ConfirmDelete
        open={!!editor.deleting}
        onClose={() => editor.setDeleting(null)}
        name={editor.deleting?.name || ""}
        body={editor.deleting?.note || "This cannot be undone."}
        blocked={editor.deleting?.blocked}
        onConfirm={deleteWorkshop}
        pending={editor.pending}
      />
    </>
  );
}
